import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Archive, Copy, Download, Check, FileText,
    Clock, Calendar, User, Video, Link, Music,
    ChevronDown, File, Save
} from 'lucide-react';
import vaultService from '../../../services/vaultService';
import { smartCopy } from '../../../utils/textFormatting';

/**
 * TranscriptionResultModal - Elegant modal for displaying transcription results
 * 
 * Features:
 * - A4 paper proportions for document-like presentation
 * - Clean typography with serif fonts for body text
 * - Action buttons: Save to Vault, Copy, Download
 * - Responsive design with smooth animations
 * - Uses React Portal for proper positioning
 */
const TranscriptionResultModal = ({
    isOpen,
    onClose,
    transcript,
    episode,
    user,
    onSaveToVault
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Format duration from seconds to readable format
    const formatDuration = useCallback((seconds) => {
        if (!seconds || isNaN(seconds)) return 'Unknown';
        const numSeconds = Number(seconds);
        const hours = Math.floor(numSeconds / 3600);
        const minutes = Math.floor((numSeconds % 3600) / 60);
        const secs = Math.floor(numSeconds % 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        }
        if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        }
        return `${secs}s`;
    }, []);

    // Format date - handles multiple date formats
    const formatDate = useCallback((date) => {
        if (!date) return 'Just now';

        let d;
        // Handle Firestore Timestamp
        if (date && typeof date.toDate === 'function') {
            d = date.toDate();
        }
        // Handle { seconds, nanoseconds } format from Firestore
        else if (date && typeof date === 'object' && 'seconds' in date) {
            d = new Date(date.seconds * 1000);
        }
        // Handle string or number
        else if (typeof date === 'string' || typeof date === 'number') {
            d = new Date(date);
        }
        // Handle Date object
        else if (date instanceof Date) {
            d = date;
        }
        else {
            return 'Just now';
        }

        // Check for invalid date
        if (isNaN(d.getTime())) {
            return 'Just now';
        }

        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    // Get source icon based on type
    const SourceIcon = useMemo(() => {
        if (!transcript?.sourceType) return FileText;
        switch (transcript.sourceType) {
            case 'youtube': return Link;
            case 'upload': return Video;
            case 'storage': return Video;
            default: return FileText;
        }
    }, [transcript?.sourceType]);

    // Handle copy to clipboard
    const handleCopy = useCallback(async () => {
        if (!transcript?.text) return;

        try {
            await smartCopy(transcript.text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    }, [transcript?.text]);

    // Handle save to vault
    const handleSaveToVault = useCallback(async () => {
        if (!transcript || !user || isSaving) return;

        setIsSaving(true);
        setSaveSuccess(false);

        try {
            const result = await vaultService.uploadTranscript(transcript, {
                episodeId: episode?.id,
                episodeTitle: episode?.episodeTitle || episode?.title,
                userId: user.uid
            });

            if (result.success) {
                setSaveSuccess(true);
                if (onSaveToVault) {
                    onSaveToVault(result.asset);
                }
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Failed to save to vault:', error);
        } finally {
            setIsSaving(false);
        }
    }, [transcript, user, episode, isSaving, onSaveToVault]);

    // Handle download
    const handleDownload = useCallback((format = 'txt') => {
        if (!transcript?.text) return;

        let content = transcript.text;
        let filename = `transcript_${transcript.id || Date.now()}`;
        let mimeType = 'text/plain';

        if (format === 'md') {
            // Markdown format with metadata
            content = `# ${transcript.title || 'Transcript'}\n\n`;
            content += `**Source:** ${transcript.sourceUrl || 'Unknown'}\n`;
            content += `**Duration:** ${formatDuration(transcript.duration)}\n`;
            content += `**Date:** ${formatDate(transcript.createdAt)}\n\n`;
            content += `---\n\n`;
            content += transcript.text;
            filename += '.md';
            mimeType = 'text/markdown';
        } else {
            filename += '.txt';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setShowDownloadMenu(false);
    }, [transcript, formatDuration, formatDate]);

    // Get word count
    const wordCount = useMemo(() => {
        const text = transcript?.text || transcript?.content;
        if (!text) return 0;
        return text.split(/\s+/).filter(Boolean).length;
    }, [transcript]);

    // Get speaker count
    const speakerCount = useMemo(() => {
        if (!transcript?.utterances || !Array.isArray(transcript.utterances)) return 0;
        const speakers = new Set(transcript.utterances.map(u => u.speaker).filter(Boolean));
        return speakers.size;
    }, [transcript?.utterances]);

    // Get transcript title
    const transcriptTitle = useMemo(() => {
        return transcript?.title || transcript?.metadata?.title || episode?.title || 'Transcript';
    }, [transcript, episode]);

    // Get transcript text (check both text and content fields)
    const transcriptText = useMemo(() => {
        return transcript?.text || transcript?.content || '';
    }, [transcript]);

    // Modal content
    const modalContent = (
        <AnimatePresence>
            {isOpen && transcript && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={onClose}
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                    {/* Modal Container - A4 proportions */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
                        style={{
                            width: 'min(90vw, 600px)',
                            height: 'min(85vh, 848px)',
                            maxHeight: '848px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        <SourceIcon size={18} className="text-indigo-200" />
                                        <span className="text-xs font-medium text-indigo-200 uppercase tracking-wider">
                                            Transcription Complete
                                        </span>
                                    </div>
                                    <h2 className="text-xl font-bold text-white truncate font-['Outfit']">
                                        {transcriptTitle}
                                    </h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Metadata Pills */}
                            <div className="flex flex-wrap gap-3 mt-4">
                                {transcript.duration > 0 && (
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm">
                                        <Clock size={14} />
                                        <span>{formatDuration(transcript.duration)}</span>
                                    </div>
                                )}
                                {speakerCount > 0 && (
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm">
                                        <User size={14} />
                                        <span>{speakerCount} speaker{speakerCount > 1 ? 's' : ''}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm">
                                    <FileText size={14} />
                                    <span>{wordCount.toLocaleString()} words</span>
                                </div>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-hidden flex flex-col">
                            {/* Transcript Text */}
                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                                <div className="prose prose-slate max-w-none">
                                    {transcript.utterances && transcript.utterances.length > 0 ? (
                                        // Speaker-labeled transcript
                                        <div className="space-y-4">
                                            {transcript.utterances.map((utterance, index) => (
                                                <div key={index} className="flex gap-3">
                                                    <div className="flex-shrink-0 w-20">
                                                        <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-md">
                                                            {utterance.speaker || `Speaker ${index + 1}`}
                                                        </span>
                                                    </div>
                                                    <p className="flex-1 text-slate-700 leading-relaxed font-serif text-[15px]">
                                                        {utterance.text}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        // Plain text transcript
                                        <p className="text-slate-700 leading-relaxed font-serif text-[15px] whitespace-pre-wrap">
                                            {transcript.text || 'No transcript text available.'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Footer with Actions */}
                            <div className="flex-shrink-0 border-t border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between gap-3">
                                    {/* Left side - Date */}
                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                        <Calendar size={14} />
                                        <span>{formatDate(transcript.createdAt)}</span>
                                    </div>

                                    {/* Right side - Action Buttons */}
                                    <div className="flex items-center gap-2">
                                        {/* Save to Vault Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleSaveToVault}
                                            disabled={isSaving || saveSuccess}
                                            className={`
                                                flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all
                                                ${saveSuccess
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200'}
                                                ${isSaving ? 'opacity-70 cursor-wait' : ''}
                                            `}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <Save size={16} className="animate-pulse" />
                                                    <span>Saving...</span>
                                                </>
                                            ) : saveSuccess ? (
                                                <>
                                                    <Check size={16} />
                                                    <span>Saved!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Archive size={16} />
                                                    <span>Save to Vault</span>
                                                </>
                                            )}
                                        </motion.button>

                                        {/* Copy Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleCopy}
                                            className={`
                                                flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all
                                                ${copySuccess
                                                    ? 'bg-green-100 text-green-700 border border-green-200'
                                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'}
                                            `}
                                        >
                                            {copySuccess ? (
                                                <>
                                                    <Check size={16} />
                                                    <span>Copied!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={16} />
                                                    <span>Copy</span>
                                                </>
                                            )}
                                        </motion.button>

                                        {/* Download Button with Dropdown */}
                                        <div className="relative">
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                                className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-sm font-bold transition-all border border-slate-200"
                                            >
                                                <Download size={16} />
                                                <span>Download</span>
                                                <ChevronDown size={14} className={`transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} />
                                            </motion.button>

                                            {/* Download Menu */}
                                            <AnimatePresence>
                                                {showDownloadMenu && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute bottom-full mb-2 right-0 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden min-w-[140px] z-10"
                                                    >
                                                        <button
                                                            onClick={() => handleDownload('txt')}
                                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                                        >
                                                            <File size={16} />
                                                            <span>.txt</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownload('md')}
                                                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                                                        >
                                                            <FileText size={16} />
                                                            <span>.md</span>
                                                        </button>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    // Render using portal to body
    return createPortal(modalContent, document.body);
};

export default TranscriptionResultModal;
